import React, { useEffect, useState, useCallback } from "react";
import Layout1 from "../../components/layout1";
import SubHeader from "../../components/subHeader";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { loaderReducer } from "../../components/toolkit/loader";
import { useDebounce } from "../../hooks/useDebounce";
import Content from "./content";
import "./index.css";
import { useCan } from "../../utils/permissions";
import { auditLogs } from "../../functions/audit";

const Index = () => {
  const can = useCan();
  const canReadAudit = can("audit", "read") || true;
  // State management (audit logs)
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [pageC, setPageC] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);

  const dispatch = useDispatch();
  const cookies = new Cookies();
  const token = cookies.get("token");

  // Fetch audit logs with pagination
  const fetchLogs = useCallback(async () => {
    if (!canReadAudit) return;
    setLoading(true);
    dispatch(loaderReducer(true));
    try {
      const res = await auditLogs(token, pageC, limit);
      const raw = res?.data || {};
      const items = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw)
            ? raw
            : [];
      const normalized = items.map((it, idx) => ({
        _id: it?._id || idx,
        createdAt: it?.createdAt || it?.Timestamp || it?.timestamp,
        user: it?.user || it?.User,
        action: it?.action || it?.Action,
        entity: it?.collectionName || it?.Entity || it?.entity,
        ip: it?.ip || it?.IP || "-",
        message: it?.message || it?.Message,
      }));
      const total = Number.isFinite(raw?.pagination?.total)
        ? raw.pagination.total
        : Number.isFinite(raw?.total)
          ? raw.total
          : Number.isFinite(raw?.count)
            ? raw.count
            : normalized.length;

      setLogs(normalized);
      if (!searchData.trim()) {
        setFilteredLogs(normalized);
      }
      setTotalLogs(total);
      setLoading(false);
      dispatch(loaderReducer(false));
    } catch (error) {
      toast.error(error?.message || "Failed to fetch audit logs");
      setLogs([]);
      setFilteredLogs([]);
      setTotalLogs(0);
      setLoading(false);
      dispatch(loaderReducer(false));
    }
  }, [canReadAudit, dispatch, pageC, limit, searchData, token]);

  // Search logs (client-side filter as fallback; API also supports search)
  const handleSearchAPI = async (value) => {
    if (!value.trim()) {
      setFilteredLogs(logs);
      setTotalLogs(logs.length);
      return;
    }

    setLoading(true);
    dispatch(loaderReducer(true));
    try {
      const v = value.toLowerCase();
      const filtered = (logs || []).filter((l) => {
        const userText = typeof l?.user === "object"
          ? [l?.user?.email, l?.user?.name, l?.user?.role?.name].filter(Boolean).join(" ")
          : l?.user;
        const s = [
          userText,
          l?.action,
          l?.entity,
          l?.ip,
          l?.message,
          l?.createdAt,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return s.includes(v);
      });
      setFilteredLogs(filtered);
      setTotalLogs(filtered.length);
      setLoading(false);
      dispatch(loaderReducer(false));
    } catch {
      setLoading(false);
      dispatch(loaderReducer(false));
    }
  };

  // No CRUD/modals for audit logs

  // Initialize & refresh on page/search change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Ensure filteredLogs is set when logs are loaded
  useEffect(() => {
    if (logs.length > 0 && !searchData.trim()) {
      setFilteredLogs(logs);
    }
  }, [logs, searchData]);

  // Debounced search function - use only one debounce mechanism
  const handleSearchDebounced = useDebounce((value) => {
    handleSearchAPI(value);
  }, 300);

  // Handle change and trigger debounce
  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchData(value);
    handleSearchDebounced(value);
  };

  // Refresh data function
  const handleRefresh = () => {
    setSearchData("");
    setPageC(1);
    setLimit(10);
    fetchLogs();
  };

  return (
    <Layout1>
      <SubHeader />
      <Content
        arg={{
          logs,
          filteredLogs,
          loading,
          searchData,
          setSearchData,
          handleSearchInput,
          pageC,
          setPageC,
          limit,
          setLimit,
          totalLogs,
          fetchLogs,
          handleRefresh
        }}
      />
    </Layout1>
  );
};

export default Index;